import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, SendIcon, ShieldIcon, CreditCardIcon, TrendingUpIcon, CheckCircleIcon, AlertTriangleIcon, GiftIcon, ClockIcon, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
type Category = 'Activity' | 'Transactions' | 'Request to Pay' | 'Marketing' | 'Others';
interface Notification {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  message: string;
  time: string;
  unread: boolean;
  category: Category;
}
const NOTIFICATIONS: Notification[] = [{
  id: '1',
  icon: SendIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Transfer Successful',
  message: 'Rs 5,000 sent to Asad Ali via Bank Alfalah',
  time: '2 min ago',
  unread: true,
  category: 'Transactions'
}, {
  id: '2',
  icon: ShieldIcon,
  iconColor: '#F37024',
  iconBg: '#F3702415',
  title: 'Security Alert',
  message: 'New login detected from Lahore. Was this you?',
  time: '15 min ago',
  unread: true,
  category: 'Activity'
}, {
  id: '3',
  icon: CreditCardIcon,
  iconColor: '#661806',
  iconBg: '#66180615',
  title: 'Bill Due Reminder',
  message: 'Your electricity bill of Rs 3,200 is due tomorrow',
  time: '1 hour ago',
  unread: true,
  category: 'Transactions'
}, {
  id: '8',
  icon: ClockIcon,
  iconColor: '#EAB308',
  iconBg: '#EAB30815',
  title: 'Payment Request',
  message: 'Rashid Khan requested Rs 2,500 for Dinner split',
  time: '2 hours ago',
  unread: true,
  category: 'Request to Pay'
}, {
  id: '4',
  icon: GiftIcon,
  iconColor: '#8B5CF6',
  iconBg: '#8B5CF615',
  title: 'Cashback Earned!',
  message: 'You earned Rs 150 cashback on your last transaction',
  time: '3 hours ago',
  unread: false,
  category: 'Marketing'
}, {
  id: '9',
  icon: ImageIcon,
  iconColor: '#EC4899',
  iconBg: '#EC489915',
  title: 'New Feature: Split Bill',
  message: 'Now you can easily split bills with friends directly from the app!',
  time: '4 hours ago',
  unread: false,
  category: 'Marketing'
}, {
  id: '5',
  icon: TrendingUpIcon,
  iconColor: '#3B82F6',
  iconBg: '#3B82F615',
  title: 'Savings Goal Update',
  message: 'You\'re 75% towards your "Emergency Fund" goal',
  time: '5 hours ago',
  unread: false,
  category: 'Activity'
}, {
  id: '6',
  icon: CheckCircleIcon,
  iconColor: '#22C55E',
  iconBg: '#22C55E15',
  title: 'Payment Received',
  message: 'Rs 25,000 received from Rashid Khan',
  time: 'Yesterday',
  unread: false,
  category: 'Transactions'
}, {
  id: '7',
  icon: AlertTriangleIcon,
  iconColor: '#EAB308',
  iconBg: '#EAB30815',
  title: 'Low Balance Alert',
  message: 'Your account balance is below Rs 5,000',
  time: 'Yesterday',
  unread: false,
  category: 'Others'
}];
interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}
export function NotificationPanel({
  isOpen,
  onClose
}: NotificationPanelProps) {
  const {
    isDark
  } = useTheme();
  const [activeCategory, setActiveCategory] = useState<Category | 'All'>('All');
  const categories: (Category | 'All')[] = ['All', 'Activity', 'Transactions', 'Request to Pay', 'Marketing', 'Others'];
  const filteredNotifications = NOTIFICATIONS.filter(n => activeCategory === 'All' || n.category === activeCategory);
  const unreadCount = NOTIFICATIONS.filter(n => n.unread).length;
  return <AnimatePresence data-id="element-1494">
      {isOpen && <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className={`fixed inset-0 z-50 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-1495">
          {/* Header */}
          <div className={`px-6 pt-6 pb-4 flex items-center justify-between sticky top-0 z-10 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-1496">
            <div className="flex items-center gap-4" data-id="element-1497">
              <button onClick={onClose} className={`p-2 -ml-2 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1498">
                <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-1499" />
              </button>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1500">
                Notifications
              </h1>
            </div>
            {unreadCount > 0 && <span className="bg-[#F37024] text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-id="element-1501">
                {unreadCount} New
              </span>}
          </div>

          {/* Categories */}
          <div className="px-6 py-2" data-id="element-1502">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2" data-id="element-1503">
              {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-[13px] font-bold whitespace-nowrap transition-all ${activeCategory === cat ? 'bg-[#F37024] text-white shadow-md' : isDark ? 'bg-[#2D1810] text-[#B8A89E] border border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#818285] border border-[#F0E8E3]'}`} data-id="element-1504">
                  {cat}
                </button>)}
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-id="element-1505">
            {filteredNotifications.map(notification => {
          const Icon = notification.icon;
          return <div key={notification.id} className={`rounded-2xl p-4 flex gap-4 relative overflow-hidden transition-all ${notification.unread ? isDark ? 'bg-[#2D1810] border border-[#F37024]/30 shadow-sm' : 'bg-white border border-[#F37024]/20 shadow-sm' : isDark ? 'bg-[#1A0E08] border border-[rgba(243,112,36,0.1)] opacity-80' : 'bg-transparent border border-[#F0E8E3] opacity-70'}`} data-id="element-1506">
                  {notification.unread && <div className="absolute top-0 left-0 w-1 h-full bg-[#F37024]" data-id="element-1507" />}
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 mt-1" style={{
              backgroundColor: notification.iconBg
            }} data-id="element-1508">
                    <Icon className="w-6 h-6" style={{
                color: notification.iconColor
              }} strokeWidth={2} data-id="element-1509" />
                  </div>
                  <div className="flex-1" data-id="element-1510">
                    <div className="flex justify-between items-start mb-1" data-id="element-1511">
                      <h4 className={`text-[15px] font-bold pr-4 ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1512">
                        {notification.title}
                      </h4>
                      <span className={`text-[11px] font-medium whitespace-nowrap ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-1513">
                        {notification.time}
                      </span>
                    </div>
                    <p className={`text-[13px] leading-relaxed mb-2 ${isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]'}`} data-id="element-1514">
                      {notification.message}
                    </p>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" style={{
                backgroundColor: notification.iconBg,
                color: notification.iconColor
              }} data-id="element-1515">
                      {notification.category}
                    </span>
                  </div>
                </div>;
        })}
            {filteredNotifications.length === 0 && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1516">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-1517">
                  <BellIcon className={`w-8 h-8 ${isDark ? 'text-[#B8A89E]/50' : 'text-[#818285]/50'}`} data-id="element-1518" />
                </div>
                <p className={`text-[15px] font-bold ${isDark ? 'text-white' : 'text-[#303030]'}`} data-id="element-1519">
                  No notifications
                </p>
                <p className={`text-[13px] mt-1 ${isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-1520">
                  You're all caught up!
                </p>
              </div>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}